import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import { Dispatch, useMemo, useRef, useState } from 'react';

export type UseDropdownControlResult = Readonly<{
    isPopupOpen: boolean;
    /**
     * Setter for popup visibility.
     */
    setIsPopupOpen: Dispatch<React.SetStateAction<boolean>>;
    /**
     * Manually trigger dropdown results pane to load more results.
     */
    loadMore: () => void;
}>;

export type UseDropdownControlOptions = Readonly<{
    onOpen?: () => void;
    onClose?: () => void;
    onLoadMore?: () => void;
}>;

export function useDropdownControl(
    options?: UseDropdownControlOptions,
): UseDropdownControlResult {
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const isPopupOpenRef = useRef(isPopupOpen);
    isPopupOpenRef.current = isPopupOpen;

    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffectSkipFirst(() => {
        if (isPopupOpen) {
            optionsRef.current?.onOpen?.();
        } else {
            optionsRef.current?.onClose?.();
        }
    }, [isPopupOpen]);

    return useMemo<UseDropdownControlResult>(
        () => ({
            isPopupOpen,
            setIsPopupOpen,
            loadMore: () => optionsRef.current?.onLoadMore?.(),
        }),
        [isPopupOpen],
    );
}
